import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { PlayCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

export default function SpokiAutomationsPage() {
  const [loading, setLoading] = useState(false);
  const [automations, setAutomations] = useState([]);
  const [form, setForm] = useState({
    automation_id: '',
    phone: '',
    first_name: '',
    last_name: '',
    email: '',
    custom_fields: '{}'
  });

  const loadAutomations = async () => {
    try {
      setLoading(true);
      const res = await api.get('/spoki/automations');
      setAutomations(res.data || []);
    } catch (error) {
      toast.error('Errore nel caricamento delle automazioni Spoki');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAutomations();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStartAutomation = async () => {
    try {
      if (!form.automation_id || !form.phone) {
        toast.error('Seleziona una automazione e inserisci il telefono');
        return;
      }
      const payload = {
        automation_id: Number(form.automation_id),
        phone: form.phone,
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        custom_fields: (() => {
          if (!form.custom_fields) return {};
          try {
            return JSON.parse(form.custom_fields);
          } catch {
            return {};
          }
        })()
      };
      const res = await api.post('/spoki/automations/start', payload);
      if (res.data && res.data.success) {
        toast.success('Automazione Spoki avviata');
      } else {
        toast.error('Errore nell’avvio dell’automazione Spoki');
      }
    } catch (error) {
      toast.error('Errore nella richiesta di automazione Spoki');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}>
        <PlayCircle size={24} />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Automazioni Spoki
        </Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow:
                '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.16)'
            }}
          >
            <CardHeader
              title="Avvio automazione"
              subheader="Seleziona una automazione configurata e i dati del contatto"
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="automation-label">Automazione</InputLabel>
                    <Select
                      labelId="automation-label"
                      label="Automazione"
                      value={form.automation_id}
                      onChange={(e) => handleChange('automation_id', e.target.value)}
                    >
                      {automations.map((a) => (
                        <MenuItem key={a.id} value={String(a.id)}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Telefono"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nome"
                    value={form.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cognome"
                    value={form.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Custom fields (JSON)"
                    value={form.custom_fields}
                    onChange={(e) => handleChange('custom_fields', e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<PlayCircle size={18} />}
                    onClick={handleStartAutomation}
                    disabled={loading}
                  >
                    Avvia automazione
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow:
                '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.16)'
            }}
          >
            <CardHeader
              title="Automazioni configurate"
              subheader="Elenco automazioni disponibili per l’avvio da gestionale"
            />
            <CardContent>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Spoki ID</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>URL API</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {automations.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.name}</TableCell>
                      <TableCell>{a.spoki_automation_id}</TableCell>
                      <TableCell>{a.account_name}</TableCell>
                      <TableCell>{a.api_url}</TableCell>
                    </TableRow>
                  ))}
                  {automations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          Nessuna automazione configurata
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

